import type {
  IAddCustomerOrderCommandParamsData,
  ICartResponseDtoData,
  ICartStatusResponseDtoData,
  ICompleteCartDtoData,
  ICustomerOrderPositionCommandData
} from '../../dtos/ProdegaApiWebshop-api-typings-generated';
import type { CartApi } from '~/features/cart/state-machine/backend-access/api';
import type { CancelCartData, ChangeDeliveryDateData, MutatePositionPayload } from '~/features/cart/state-machine/types';
import ky, { HTTPError } from 'ky';
import i18n from 'i18next';
import type { ProlixApplicationError } from '../../common/types/prolixApplicationError';
import { isProlixProApplicationError } from '../../common/types/prolixApplicationError';
import {
  HEADER_PROLIX_APPLICATION_ERROR,
  toWebshopApiError,
  WebshopApiErrorException
} from '~/common/types/webshopApiError';
import { logToConsole } from '~/logging/consoleLogger';

const loggerName = 'cart-api';

const DEFAULT_TECHNICAL_MESSAGE =
  'Ein technischer Fehler ist aufgetreten. Bitte laden Sie den Webshop neu!';

export function createApi(baseUrl: string): CartApi {
  const kyInstance = ky.create({
    prefix: baseUrl,
    timeout: 300_000,
    headers: {
      accept: 'application/json'
    },
    hooks: {
      beforeRequest: [
        ({ request }) => {
          if (!request.headers.has('Accept-Language') && i18n.language) {
            request.headers.set('Accept-Language', i18n.language);
          }
        }
      ],
      afterResponse: [
        async ({ response }) => {
          const headerMarker = response.headers.get(HEADER_PROLIX_APPLICATION_ERROR) === 'true';
          let isProlixError = headerMarker;
          let cachedBody: ICartResponseDtoData[] | ProlixApplicationError | undefined;

          if (!isProlixError) {
            const contentType = response.headers.get('content-type');
            if (!contentType?.includes('application/json')) {
              return;
            }
            try {
              cachedBody = (await response.clone().json()) as
                | ICartResponseDtoData[]
                | ProlixApplicationError;
              isProlixError = isProlixProApplicationError(cachedBody);
            } catch {
              return;
            }
          }

          if (isProlixError) {
            logToConsole({
              loggerName,
              logLevel: 'INFO',
              message: 'Cart API: a Prolix Application Error was returned'
            });
            const apiError = await toWebshopApiError(response, DEFAULT_TECHNICAL_MESSAGE);
            throw new WebshopApiErrorException(apiError);
          }
        }
      ],
      beforeError: [
        async (state): Promise<Error> => {
          if (state.error instanceof HTTPError) {
            const apiError = await toWebshopApiError(state.error.response, DEFAULT_TECHNICAL_MESSAGE);
            return new WebshopApiErrorException(apiError);
          }
          return state.error;
        }
      ]
    }
  });

  async function fetchCurrentOrder(frontendOrderId: number | undefined) {
    const response = await kyInstance.get('', { searchParams: frontendOrderId ? { frontendOrderId } : undefined });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function createNewOrder(deliveryDate: string) {
    const params = {
      deliveryDate
    };
    const response = await kyInstance.post('', {
      json: params
    });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function refreshCurrentOrder(frontendOrderId: number | undefined) {
    const response = await kyInstance.post(`refreshCart`, {
      searchParams: frontendOrderId ? { frontendOrderId } : undefined
    });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function cancelCurrentOrder(cancelCartData: CancelCartData) {
    const response = await kyInstance.post(`cancel`, {
      json: cancelCartData
    });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function changeDeliveryDateOfCurrentOrder(changeDeliveryDateData: ChangeDeliveryDateData) {
    const response = await kyInstance.post(`changedeliverydate`, {
      json: changeDeliveryDateData
    });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function sendCurrentOrder(cartParams: ICompleteCartDtoData) {
    const response = await kyInstance.post(`send`, {
      json: {
        cartParams
      }
    });
    const data = await response.json<ICartResponseDtoData>();
    return data;
  }

  async function addPosition(orderId: number, articleNumber: string, quantity: number, externalArticle: boolean) {
    const payload: IAddCustomerOrderCommandParamsData[] = [
      {
        orderId: orderId,
        articleNumber,
        quantity: quantity,
        externalArticle: externalArticle,
        articleIsEan: false
      }
    ];
    const response = await kyInstance.post(`add`, {
      json: payload
    });
    const data = await response.json<ICartResponseDtoData[]>();
    if (data[0].messages && data[0].messages.length > 0) {
      logToConsole({
        loggerName,
        logLevel: 'INFO',
        message: `ERROR adding article ${articleNumber} to order ${orderId}: ${data[0].messages}`
      });
    }

    const addPositionResponseData = data[0];
    return addPositionResponseData;
  }

  async function removePosition(positionId: number, externalArticle: boolean) {
    const payload = { positionId, externalArticle };
    const response = await kyInstance.post(`removePosition`, {
      json: payload
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function mutatePosition(positionId: number, payload: MutatePositionPayload) {
    const data = {
      positionId,
      payload
    };
    // Die WAF hat ursprünglich PUT Requests blockiert, deshalb wurde zu dem Zeitpunkt hier ein POST Request gemacht
    const response = await kyInstance.post(`mutatePosition/${positionId}`, {
      json: data
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function sendPositonCommands(positionCommands: ICustomerOrderPositionCommandData[]) {
    const response = await kyInstance.post(`positionCommands`, {
      json: positionCommands
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function copyExistingOrderToCart(currentOrderId: number, existingOrderId: number) {
    const response = await kyInstance.post(`copyExistingOrderToCart`, {
      json: { currentOrderId, existingOrderId }
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function copyTemplateArticleToCart(currentOrderId: number, templateId: number, selectedMainArticleIds?: number[]) {
    const response = await kyInstance.post(`copyTemplateArticleToCart`, {
      json: { currentOrderId, templateId, selectedMainArticleIds }
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function copySalesOrderSetArticleToCart(currentOrderId: number, articles: { articleNumber: string; quantity: number }[]) {
    const response = await kyInstance.post(`copySalesOrderSetArticleToCart`, {
      json: { currentOrderId, articles }
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function importCsvToCart(currentOrderId: number, csvText: string) {
    const response = await kyInstance.post(`importCsvToCart`, {
      json: { currentOrderId, csvFile: csvText }
    });
    const responseData = await response.json<ICartResponseDtoData>();
    return responseData;
  }

  async function fetchCartStatus(frontendOrderId: number | undefined) {
    const response = await kyInstance.get('status', {
      searchParams: frontendOrderId ? { frontendOrderId } : undefined
    });
    const data = await response.json<ICartStatusResponseDtoData>();
    return data;
  }

  return {
    fetchCurrentOrder,
    refreshCurrentOrder,
    createNewOrder,
    cancelCurrentOrder,
    sendCurrentOrder,
    changeDeliveryDateOfCurrentOrder,
    addPosition,
    removePosition,
    mutatePosition,
    sendPositonCommands,
    copyExistingOrderToCart,
    copyTemplateArticleToCart,
    copySalesOrderSetArticleToCart,
    importCsvToCart,
    fetchCartStatus
  };
}
